import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { DropdownMenu, IconButton, Typography, useStateInRef } from 'seagull';
import { ArrowLeft, ArrowRight, Copy, Edit, Kebab, Trash } from 'seagull/icons';
import { useLocaleKeys } from '~/i18n';
import { agentTargetForBundle } from '../AgentChangeHighlight/agentTargetSets';
import styles from './ServicePackages.module.scss';
const tabPanelId = 'services-in-pkg';
export default function ServicePackagesTab(props) {
    const { bundle, isSelected, testId, bundlesCount, bundleIndex, isReadonly, isDragActive, dragHandle, onClick, onRemove, onChangeName, onDuplicate: handleDuplicate, onMoveLeft, onMoveRight } = props;
    const { t } = useLocaleKeys('agreementStudio.servicePackages.tab');
    const getProps = useStateInRef(props);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState(bundle.name);
    const inputRef = useRef(null);
    useEffect(()=>{
        if (isEditingName) {
            const cancelEditOnEscapeKey = (e)=>{
                if (e.key === 'Escape' && !e.defaultPrevented) {
                    setEditedName(getProps().bundle.name);
                    setIsEditingName(false);
                }
            };
            window.addEventListener('keydown', cancelEditOnEscapeKey);
            return ()=>window.removeEventListener('keydown', cancelEditOnEscapeKey);
        }
    }, [
        isEditingName
    ]);
    const applyEdit = ()=>{
        onChangeName(editedName.trim(), bundle.id);
        setIsEditingName(false);
    };
    const onRename = ()=>{
        onClick(bundle.id);
        setIsEditingName(true);
        setTimeout(()=>inputRef.current.focus());
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs("button", {
            role: "tab",
            className: styles.tab,
            "aria-selected": isSelected ? 'true' : 'false',
            "aria-controls": tabPanelId,
            "data-testid": testId,
            "data-agent-target": agentTargetForBundle(bundle.id),
            onClick: ()=>onClick(bundle.id),
            onDoubleClick: onRename,
            children: [
                dragHandle && /*#__PURE__*/ _jsx("span", {
                    onClick: (e)=>e.stopPropagation(),
                    onDoubleClick: (e)=>e.stopPropagation(),
                    children: dragHandle
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: styles.tabContent,
                    children: isEditingName ? /*#__PURE__*/ _jsx("form", {
                        onSubmit: (e)=>{
                            e.preventDefault();
                            applyEdit();
                        },
                        className: styles.form,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            as: "input",
                            ref: inputRef,
                            className: styles.nameInput,
                            testId: "".concat(testId, "-input"),
                            value: editedName,
                            onChange: (e)=>setEditedName(e.target.value),
                            onBlur: ()=>applyEdit()
                        })
                    }) : /*#__PURE__*/ _jsx(Typography, {
                        ellipsis: true,
                        children: bundle.name
                    })
                }),
                !isEditingName && !isReadonly && !isDragActive && /*#__PURE__*/ _jsxs(DropdownMenu, {
                    onClose: (anchorEl, reason, event)=>event.stopPropagation(),
                    target: /*#__PURE__*/ _jsx(IconButton, {
                        bi: {
                            name: 'open_service_package_actions_menu'
                        },
                        noPadding: true,
                        skin: "transparent",
                        size: "small",
                        testId: "".concat(testId, "-dropdown"),
                        onClick: (e)=>e.stopPropagation(),
                        children: /*#__PURE__*/ _jsx(Kebab, {})
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                            bi: {
                                name: 'edit'
                            },
                            iconStart: /*#__PURE__*/ _jsx("div", {
                                className: styles.iconContainer,
                                children: /*#__PURE__*/ _jsx(Edit, {
                                    size: 18
                                })
                            }),
                            testId: "".concat(testId, "-edit"),
                            onClick: (e)=>{
                                e.stopPropagation();
                                onRename();
                            },
                            children: t.rename()
                        }),
                        bundlesCount < 3 && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                            bi: {
                                name: 'duplicate'
                            },
                            iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                            testId: "".concat(testId, "-duplicate"),
                            onClick: (e)=>{
                                e.stopPropagation();
                                handleDuplicate(bundle);
                            },
                            children: t.duplicate()
                        }),
                        onMoveLeft && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                            bi: {
                                name: 'move_left'
                            },
                            iconStart: /*#__PURE__*/ _jsx(ArrowLeft, {}),
                            testId: "".concat(testId, "-move-left"),
                            disabled: bundleIndex === 0,
                            onClick: (e)=>{
                                e.stopPropagation();
                                onMoveLeft();
                            },
                            children: t.moveLeft()
                        }),
                        onMoveRight && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                            bi: {
                                name: 'move_right'
                            },
                            iconStart: /*#__PURE__*/ _jsx(ArrowRight, {}),
                            testId: "".concat(testId, "-move-right"),
                            disabled: bundleIndex === bundlesCount - 1,
                            onClick: (e)=>{
                                e.stopPropagation();
                                onMoveRight();
                            },
                            children: t.moveRight()
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                            bi: {
                                name: 'remove'
                            },
                            skin: "error",
                            testId: "".concat(testId, "-remove"),
                            iconStart: /*#__PURE__*/ _jsx("div", {
                                className: styles.iconContainer,
                                children: /*#__PURE__*/ _jsx(Trash, {
                                    size: 20
                                })
                            }),
                            onClick: (e)=>{
                                e.stopPropagation();
                                onClick(bundle.id);
                                onRemove(bundle.id);
                            },
                            children: t.remove()
                        })
                    ]
                })
            ]
        }, bundle.id)
    });
}
